import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Logo from "app/components/Logo/Logo";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  AppLanguage,
  selectLanguage,
  setLanguage,
} from "features/language/languageSlice";
import I18n from "i18n";
import moment from "moment";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SelfSeatingMap from "./SelfSeatingMap";
import {
  canAddTable,
  getTables,
  initialSelection,
  selectedCapacity,
  selectedTables,
  tableKey,
  toSelectionPayload,
} from "./selectionUtils";
import {
  identifyBooking,
  loadSelfSeating,
  saveSelfSeating,
  SelfSeatingApiError,
  SelfSeatingLayoutObject,
  SelfSeatingState,
} from "./selfSeatingApi";

const TOKEN_PATTERN = /^[a-f0-9]{64}$/;
const TABLET_TOKEN_KEY = "self-seating-token";

type PageError = "invalid" | "expired" | "unavailable" | "offline" | "unknown";

export default function SelfSeatingPage() {
  const { token: routeToken } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const language = useAppSelector(selectLanguage);
  const [data, setData] = useState<SelfSeatingState>();
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [layoutIndex, setLayoutIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState<PageError>();
  const [notice, setNotice] = useState<string>();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [completed, setCompleted] = useState(false);
  const token =
    routeToken && TOKEN_PATTERN.test(routeToken) ? routeToken : undefined;
  const isTabletSession =
    !!token && sessionStorage.getItem(TABLET_TOKEN_KEY) === token;

  const resetTablet = () => {
    sessionStorage.removeItem(TABLET_TOKEN_KEY);
    navigate("/self-seating", { replace: true });
  };

  const applyState = (state: SelfSeatingState, nextSelection?: Set<string>) => {
    setData(state);
    setSelection(nextSelection || initialSelection(state.layouts));
    setLayoutIndex(current =>
      Math.min(current, Math.max(0, state.layouts.length - 1)),
    );
    setCompleted(state.booking.completed);
    setPageError(undefined);
  };

  const load = async () => {
    if (!routeToken) {
      return;
    }
    if (!TOKEN_PATTERN.test(routeToken)) {
      setPageError("invalid");
      return;
    }
    setLoading(true);
    setPageError(undefined);
    try {
      applyState(await loadSelfSeating(routeToken));
    } catch (error) {
      setPageError(mapLoadError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(undefined);
    setSelection(new Set());
    setCompleted(false);
    setNotice(undefined);
    load();
  }, [routeToken]);

  useEffect(() => {
    if (!completed || !isTabletSession) {
      return;
    }

    const timeout = window.setTimeout(resetTablet, 4000);
    return () => window.clearTimeout(timeout);
  }, [completed, isTabletSession]);

  const capacity = useMemo(
    () => (data ? selectedCapacity(data.layouts, selection) : 0),
    [data, selection],
  );
  const selected = useMemo(
    () => (data ? selectedTables(data.layouts, selection) : []),
    [data, selection],
  );
  const remaining = data ? Math.max(0, data.booking.guest_count - capacity) : 0;
  const canSubmit =
    !!data && capacity >= data.booking.guest_count && selected.length > 0;

  const toggleTable = (layoutId: number, table: SelfSeatingLayoutObject) => {
    if (!data) {
      return;
    }
    const key = tableKey(layoutId, table.id);
    setSelection(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else if (
        canAddTable(data.layouts, current, data.booking.guest_count, table)
      ) {
        next.add(key);
      }
      return next;
    });
  };

  const submit = async () => {
    if (!data || !token || !canSubmit || saving) {
      return;
    }
    setConfirmOpen(false);
    setSaving(true);
    setNotice(undefined);
    try {
      const refreshed = await saveSelfSeating(
        token,
        toSelectionPayload(data.layouts, selection),
      );
      applyState(refreshed);
      setCompleted(true);
    } catch (error) {
      if (error instanceof SelfSeatingApiError && error.status === 409) {
        try {
          const refreshed = await loadSelfSeating(token);
          const availableKeys = new Set(
            getTables(refreshed.layouts)
              .filter(
                ({ table }) =>
                  table.selected ||
                  (!table.occupied && table.available !== false),
              )
              .map(({ layoutId, table }) => tableKey(layoutId, table.id)),
          );
          const stillValid = new Set(
            Array.from(selection).filter(key => availableKeys.has(key)),
          );
          applyState(refreshed, stillValid);
          setCompleted(false);
          setNotice(I18n.t("SelfSeating.availabilityChanged"));
        } catch (reloadError) {
          setPageError(mapLoadError(reloadError));
        }
      } else if (error instanceof SelfSeatingApiError && error.status === 422) {
        setNotice(I18n.t("SelfSeating.selectionInvalid"));
      } else {
        setPageError(mapLoadError(error));
      }
    } finally {
      setSaving(false);
    }
  };

  const changeLanguage = (nextLanguage: AppLanguage) => {
    dispatch(setLanguage(nextLanguage));
    moment.locale(nextLanguage);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        color: "#173c33",
        backgroundColor: "#f4f6f3",
        backgroundImage:
          "linear-gradient(120deg, rgba(0,121,107,.08), transparent 36%), repeating-linear-gradient(90deg, transparent, transparent 47px, rgba(23,60,51,.025) 48px)",
      }}
    >
      <Box
        component="header"
        sx={{
          height: 72,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid rgba(23,60,51,.12)",
          backgroundColor: "rgba(255,255,255,.92)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Logo size={42} />
          <Box>
            <Typography fontWeight={800} lineHeight={1.1} color="#173c33">
              Silverline
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {I18n.t("SelfSeating.header")}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <FormControl size="small" sx={{ minWidth: 112 }}>
            <InputLabel id="self-seating-language-label">
              {I18n.t("SelfSeating.language")}
            </InputLabel>
            <Select
              labelId="self-seating-language-label"
              value={language}
              label={I18n.t("SelfSeating.language")}
              onChange={event =>
                changeLanguage(event.target.value as AppLanguage)
              }
            >
              <MenuItem value="hu">Magyar</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>
        </Container>
      </Box>

      {!routeToken ? (
        <IdentifyView
          onIdentified={(nextToken, state) => {
            sessionStorage.setItem(TABLET_TOKEN_KEY, nextToken);
            applyState(state);
            navigate(`/self-seating/${nextToken}`, { replace: true });
          }}
        />
      ) : loading ? (
        <CenteredState>
          <CircularProgress size={42} />
          <Typography variant="h6">{I18n.t("SelfSeating.loading")}</Typography>
        </CenteredState>
      ) : pageError ? (
        <ErrorView
          error={pageError}
          onRetry={load}
          onIdentify={() => navigate("/self-seating")}
        />
      ) : data && completed ? (
        <SuccessView
          data={data}
          tabletMode={isTabletSession}
          onEdit={() => setCompleted(false)}
          onNext={resetTablet}
        />
      ) : data ? (
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
          <Stack spacing={2.5}>
            <EventSummary
              data={data}
              capacity={capacity}
              remaining={remaining}
            />

            {!!notice && (
              <Alert severity="warning" onClose={() => setNotice(undefined)}>
                {notice}
              </Alert>
            )}

            {data.layouts.length === 0 ? (
              <EmptyLayouts onRetry={load} />
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    lg: "minmax(0, 1fr) 320px",
                  },
                  gap: 2.5,
                  alignItems: "start",
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{ overflow: "hidden", borderRadius: 2 }}
                >
                  <Tabs
                    value={layoutIndex}
                    onChange={(_, value) => setLayoutIndex(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label={I18n.t("SelfSeating.levels")}
                    sx={{
                      px: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {data.layouts.map(layout => (
                      <Tab key={layout.id} label={layout.name} />
                    ))}
                  </Tabs>
                  <SelfSeatingMap
                    layout={data.layouts[layoutIndex]}
                    selectedIds={
                      new Set(
                        data.layouts[layoutIndex].layout
                          .filter(table =>
                            selection.has(
                              tableKey(data.layouts[layoutIndex].id, table.id),
                            ),
                          )
                          .map(table => table.id),
                      )
                    }
                    onToggle={table =>
                      toggleTable(data.layouts[layoutIndex].id, table)
                    }
                    canSelect={table =>
                      canAddTable(
                        data.layouts,
                        selection,
                        data.booking.guest_count,
                        table,
                      )
                    }
                  />
                  <MapLegend />
                </Paper>

                <SelectionPanel
                  data={data}
                  capacity={capacity}
                  remaining={remaining}
                  selected={selected.map(({ layoutId, table }) => ({
                    layoutName:
                      data.layouts.find(layout => layout.id === layoutId)
                        ?.name || "",
                    table,
                  }))}
                  canSubmit={canSubmit}
                  saving={saving}
                  onConfirm={() => setConfirmOpen(true)}
                />
              </Box>
            )}
          </Stack>
        </Container>
      ) : null}

      <Dialog
        open={confirmOpen}
        onClose={() => !saving && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{I18n.t("SelfSeating.confirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {I18n.t("SelfSeating.confirmText")}
          </Typography>
          <Stack spacing={1}>
            {selected.map(({ layoutId, table }) => (
              <Box
                key={tableKey(layoutId, table.id)}
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Typography fontWeight={700}>
                  {I18n.t("SelfSeating.tableNumber", { number: table.name })}
                </Typography>
                <Typography color="text.secondary">
                  {table.max_person || 0} {I18n.t("SelfSeating.personShort")}
                </Typography>
              </Box>
            ))}
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography fontWeight={800}>
                {I18n.t("SelfSeating.totalCapacity")}
              </Typography>
              <Typography fontWeight={800}>{capacity}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            {I18n.t("SelfSeating.back")}
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : undefined}
          >
            {I18n.t("SelfSeating.confirmSelection")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function IdentifyView({
  onIdentified,
}: {
  onIdentified: (token: string, state: SelfSeatingState) => void;
}) {
  const [bookingNumber, setBookingNumber] = useState("");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!bookingNumber.trim() || !/^\d{4}$/.test(pin)) {
      setError(I18n.t("SelfSeating.identifyValidation"));
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      const response = await identifyBooking(bookingNumber.trim(), pin);
      onIdentified(response.token, response.data);
    } catch (requestError) {
      const status =
        requestError instanceof SelfSeatingApiError ? requestError.status : 0;
      setError(
        status === 429
          ? I18n.t("SelfSeating.tooManyAttempts")
          : status === 409
            ? I18n.t("SelfSeating.unavailableMessage")
            : status === 0
              ? I18n.t("SelfSeating.offlineMessage")
              : I18n.t("SelfSeating.identifyGenericError"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, sm: 9 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="overline" color="primary.main" fontWeight={800}>
          {I18n.t("SelfSeating.tabletEyebrow")}
        </Typography>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: "2rem", sm: "2.7rem" },
            fontWeight: 800,
            letterSpacing: 0,
          }}
        >
          {I18n.t("SelfSeating.identifyTitle")}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 520 }}>
          {I18n.t("SelfSeating.identifyIntro")}
        </Typography>
      </Box>
      <Paper
        component="form"
        onSubmit={submit}
        variant="outlined"
        sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 2 }}
      >
        <Stack spacing={2.5}>
          {!!error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={I18n.t("SelfSeating.bookingNumber")}
            value={bookingNumber}
            onChange={event => setBookingNumber(event.target.value)}
            inputProps={{ autoComplete: "off", maxLength: 64 }}
            required
            fullWidth
          />
          <TextField
            label={I18n.t("SelfSeating.pin")}
            type="password"
            value={pin}
            onChange={event => setPin(event.target.value.replace(/\D/g, ""))}
            inputProps={{
              inputMode: "numeric",
              autoComplete: "one-time-code",
              maxLength: 4,
            }}
            required
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            sx={{ minHeight: 48 }}
          >
            {submitting
              ? I18n.t("SelfSeating.identifying")
              : I18n.t("SelfSeating.continue")}
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            {I18n.t("SelfSeating.identifyPrivacy")}
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
}

function EventSummary({
  data,
  capacity,
  remaining,
}: {
  data: SelfSeatingState;
  capacity: number;
  remaining: number;
}) {
  return (
    <Box>
      <Typography variant="overline" color="primary.main" fontWeight={800}>
        {data.booking.booking_number}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, letterSpacing: 0, mb: 2 }}
      >
        {data.event.program_name}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 3 }}>
        <SummaryItem
          icon={<CalendarMonthRoundedIcon />}
          label={moment(data.event.start_time).format("L LT")}
        />
        <SummaryItem
          icon={<GroupsRoundedIcon />}
          label={I18n.t("SelfSeating.guestsValue", {
            count: data.booking.guest_count,
          })}
        />
        <SummaryItem
          icon={<EventSeatRoundedIcon />}
          label={I18n.t("SelfSeating.capacityValue", { count: capacity })}
          emphasized={remaining === 0}
        />
      </Stack>
    </Box>
  );
}

function SummaryItem({
  icon,
  label,
  emphasized = false,
}: {
  icon: React.ReactNode;
  label: string;
  emphasized?: boolean;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ color: emphasized ? "#00796b" : "text.secondary" }}
    >
      {icon}
      <Typography fontWeight={emphasized ? 800 : 600}>{label}</Typography>
    </Stack>
  );
}

function SelectionPanel({
  data,
  capacity,
  remaining,
  selected,
  canSubmit,
  saving,
  onConfirm,
}: {
  data: SelfSeatingState;
  capacity: number;
  remaining: number;
  selected: { layoutName: string; table: SelfSeatingLayoutObject }[];
  canSubmit: boolean;
  saving: boolean;
  onConfirm: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        position: { lg: "sticky" },
        top: { lg: 20 },
      }}
    >
      <Typography variant="h6" fontWeight={800}>
        {I18n.t("SelfSeating.selectionTitle")}
      </Typography>
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{ mt: 0.5, mb: 2 }}
      >
        {remaining > 0
          ? I18n.t("SelfSeating.remainingSeats", { count: remaining })
          : I18n.t("SelfSeating.enoughSeats")}
      </Typography>
      <Stack spacing={1} sx={{ minHeight: 54 }}>
        {selected.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {I18n.t("SelfSeating.noSelection")}
          </Typography>
        ) : (
          selected.map(({ layoutName, table }) => (
            <Box
              key={`${layoutName}-${table.id}`}
              sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
            >
              <Box>
                <Typography fontWeight={700}>
                  {I18n.t("SelfSeating.tableNumber", { number: table.name })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {layoutName}
                </Typography>
              </Box>
              <Typography fontWeight={700}>
                {table.max_person || 0} {I18n.t("SelfSeating.personShort")}
              </Typography>
            </Box>
          ))
        )}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography fontWeight={700}>
          {I18n.t("SelfSeating.totalCapacity")}
        </Typography>
        <Typography
          fontWeight={900}
          color={canSubmit ? "#00796b" : "text.primary"}
        >
          {capacity} / {data.booking.guest_count}
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={!canSubmit || saving}
        onClick={onConfirm}
        sx={{ minHeight: 48 }}
      >
        {I18n.t("SelfSeating.reviewSelection")}
      </Button>
    </Paper>
  );
}

function MapLegend() {
  return (
    <Stack
      direction="row"
      useFlexGap
      flexWrap="wrap"
      spacing={2}
      sx={{ px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}
    >
      <Legend
        color="#fff"
        border="#357b67"
        label={I18n.t("SelfSeating.tableAvailable")}
      />
      <Legend
        color="#00796b"
        border="#00695c"
        label={I18n.t("SelfSeating.tableSelected")}
        mark="✓"
      />
      <Legend
        color="#d5d9d7"
        border="#7d8582"
        label={I18n.t("SelfSeating.tableOccupied")}
        mark="×"
      />
    </Stack>
  );
}

function Legend({
  color,
  border,
  label,
  mark,
}: {
  color: string;
  border: string;
  label: string;
  mark?: string;
}) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          border: `2px solid ${border}`,
          backgroundColor: color,
          color: "white",
          fontSize: 12,
          fontWeight: 900,
          display: "grid",
          placeItems: "center",
        }}
      >
        {mark}
      </Box>
      <Typography variant="caption" fontWeight={700}>
        {label}
      </Typography>
    </Stack>
  );
}

function SuccessView({
  data,
  tabletMode,
  onEdit,
  onNext,
}: {
  data: SelfSeatingState;
  tabletMode: boolean;
  onEdit: () => void;
  onNext: () => void;
}) {
  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, sm: 9 } }}>
      <Box sx={{ textAlign: "center" }}>
        <CheckCircleRoundedIcon
          sx={{ fontSize: 72, color: "#00796b", mb: 2 }}
        />
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: "2rem", sm: "2.6rem" },
            fontWeight: 800,
            letterSpacing: 0,
          }}
        >
          {I18n.t("SelfSeating.successTitle")}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1.5 }}>
          {I18n.t("SelfSeating.successMessage")}
        </Typography>
      </Box>
      <Paper
        variant="outlined"
        sx={{ mt: 4, p: { xs: 2.5, sm: 3.5 }, borderRadius: 2 }}
      >
        <Typography variant="overline" color="primary.main" fontWeight={800}>
          {data.booking.booking_number}
        </Typography>
        <Typography variant="h6" fontWeight={800}>
          {data.event.program_name}
        </Typography>
        <Typography color="text.secondary">
          {moment(data.event.start_time).format("L LT")}
        </Typography>
        <Divider sx={{ my: 2.5 }} />
        <Typography fontWeight={700} sx={{ mb: 1 }}>
          {I18n.t("SelfSeating.yourTables")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
          {data.booking.table_numbers.map(number => (
            <Box
              key={number}
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                color: "#00695c",
                backgroundColor: alpha("#00796b", 0.1),
                fontWeight: 800,
              }}
            >
              {number}
            </Box>
          ))}
        </Stack>
        {tabletMode ? (
          <Stack spacing={1.5} sx={{ mt: 3 }}>
            <Button variant="contained" size="large" onClick={onNext}>
              {I18n.t("SelfSeating.nextGuest")}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {I18n.t("SelfSeating.tabletResetMessage")}
            </Typography>
          </Stack>
        ) : (
          <Button
            onClick={onEdit}
            startIcon={<ArrowBackRoundedIcon />}
            sx={{ mt: 3 }}
          >
            {I18n.t("SelfSeating.editSelection")}
          </Button>
        )}
      </Paper>
    </Container>
  );
}

function ErrorView({
  error,
  onRetry,
  onIdentify,
}: {
  error: PageError;
  onRetry: () => void;
  onIdentify: () => void;
}) {
  const title =
    error === "expired"
      ? I18n.t("SelfSeating.expiredTitle")
      : error === "unavailable"
        ? I18n.t("SelfSeating.unavailableTitle")
        : error === "offline"
          ? I18n.t("SelfSeating.offlineTitle")
          : I18n.t("SelfSeating.errorTitle");
  const message =
    error === "expired"
      ? I18n.t("SelfSeating.expiredMessage")
      : error === "unavailable"
        ? I18n.t("SelfSeating.unavailableMessage")
        : error === "offline"
          ? I18n.t("SelfSeating.offlineMessage")
          : error === "invalid"
            ? I18n.t("SelfSeating.invalidMessage")
            : I18n.t("SelfSeating.unknownMessage");
  return (
    <CenteredState>
      <ErrorOutlineRoundedIcon sx={{ fontSize: 64, color: "#b24c3b" }} />
      <Typography variant="h4" fontWeight={800}>
        {title}
      </Typography>
      <Typography color="text.secondary" textAlign="center" maxWidth={480}>
        {message}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        {(error === "offline" || error === "unknown") && (
          <Button
            variant="contained"
            startIcon={<RefreshRoundedIcon />}
            onClick={onRetry}
          >
            {I18n.t("SelfSeating.retry")}
          </Button>
        )}
        <Button variant="outlined" onClick={onIdentify}>
          {I18n.t("SelfSeating.identifyAgain")}
        </Button>
      </Stack>
    </CenteredState>
  );
}

function EmptyLayouts({ onRetry }: { onRetry: () => void }) {
  return (
    <Paper
      variant="outlined"
      sx={{ py: 8, px: 2, textAlign: "center", borderRadius: 2 }}
    >
      <EventSeatRoundedIcon sx={{ fontSize: 52, color: "text.disabled" }} />
      <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>
        {I18n.t("SelfSeating.emptyTitle")}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {I18n.t("SelfSeating.emptyMessage")}
      </Typography>
      <Button startIcon={<RefreshRoundedIcon />} onClick={onRetry}>
        {I18n.t("SelfSeating.retry")}
      </Button>
    </Paper>
  );
}

function CenteredState({ children }: { children: React.ReactNode }) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={2}
      sx={{ minHeight: "calc(100vh - 72px)", px: 2 }}
    >
      {children}
    </Stack>
  );
}

function mapLoadError(error: unknown): PageError {
  if (!(error instanceof SelfSeatingApiError)) {
    return "unknown";
  }
  if (error.status === 0) return "offline";
  if (error.status === 410) return "expired";
  if (error.status === 409) return "unavailable";
  if (error.status === 404) return "invalid";
  return "unknown";
}
